import React, { useState, useEffect } from "react";
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";

// Simple inline SVG icons
const SunIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
  </svg>
);

const DropletIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
  </svg>
);

const MoonIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const LeafIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
  </svg>
);

const FlameIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6" />
  </svg>
);

const CloudIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
  </svg>
);

const PaletteIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="13.5" cy="6.5" r=".5" />
    <circle cx="17.5" cy="10.5" r=".5" />
    <circle cx="8.5" cy="7.5" r=".5" />
    <circle cx="6.5" cy="12.5" r=".5" />
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
  </svg>
);

// Define available themes
const themes = [
  {
    id: "default",
    name: "Default",
    description: "Clean and professional",
    icon: <PaletteIcon />,
    primaryColor: "#20A8E0",
    secondaryColor: "#F9E719",
    cssFile: "/themes/default.css",
  },
  {
    id: "sunrise",
    name: "Sunrise",
    description: "Warm and energetic",
    icon: <SunIcon />,
    primaryColor: "#FF6B35",
    secondaryColor: "#F7931E",
    cssFile: "/themes/sunrise.css",
  },
  {
    id: "ocean",
    name: "Ocean",
    description: "Cool and calm",
    icon: <DropletIcon />,
    primaryColor: "#006994",
    secondaryColor: "#0EA5E9",
    cssFile: "/themes/ocean.css",
  },
  {
    id: "midnight",
    name: "Midnight",
    description: "Dark and elegant",
    icon: <MoonIcon />,
    primaryColor: "#1E293B",
    secondaryColor: "#7C3AED",
    cssFile: "/themes/midnight.css",
  },
  {
    id: "forest",
    name: "Forest",
    description: "Natural and fresh",
    icon: <LeafIcon />,
    primaryColor: "#059669",
    secondaryColor: "#10B981",
    cssFile: "/themes/forest.css",
  },
  {
    id: "sunset",
    name: "Sunset",
    description: "Vibrant and bold",
    icon: <FlameIcon />,
    primaryColor: "#DC2626",
    secondaryColor: "#FB923C",
    cssFile: "/themes/sunset.css",
  },
  {
    id: "cloudy",
    name: "Cloudy",
    description: "Soft and minimal",
    icon: <CloudIcon />,
    primaryColor: "#64748B",
    secondaryColor: "#94A3B8",
    cssFile: "/themes/cloudy.css",
  },
];

const ThemePicker = ({ value = "default", onChange, disabled = false }) => {
  const { t } = useTranslation();
  const [currentTheme, setCurrentTheme] = useState(value);
  const [themeStyleElement, setThemeStyleElement] = useState(null);

  // Load theme CSS dynamically
  const loadThemeCSS = (themeId) => {
    const theme = themes.find((t) => t.id === themeId);
    if (!theme) return;

    // Remove existing theme stylesheet if present
    const existingLink = document.getElementById("theme-stylesheet");
    if (existingLink) {
      document.head.removeChild(existingLink);
    }

    // Create new link element for theme CSS
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = theme.cssFile;
    link.id = "theme-stylesheet";

    // Add to document head
    document.head.appendChild(link);
    setThemeStyleElement(link);

    // Store theme in localStorage for persistence
    localStorage.setItem("appTheme", themeId);
  };

  // Initialize theme on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem("appTheme") || value;
    setCurrentTheme(savedTheme);
    loadThemeCSS(savedTheme);
  }, [value]);

  const handleThemeChange = (event) => {
    const newTheme = event.target.value;
    setCurrentTheme(newTheme);
    loadThemeCSS(newTheme);

    // Notify parent component
    if (onChange) {
      onChange({ target: { name: "theme", value: newTheme } });
    }
  };

  const selectedTheme = themes.find((t) => t.id === currentTheme) || themes[0];

  return (
    <FormControl fullWidth>
      <Select
        value={currentTheme}
        onChange={handleThemeChange}
        disabled={disabled}
        sx={{
          height: "48px",
          borderRadius: "8px",
          backgroundColor: "#FFFFFF",
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            gap: 1,
          },
        }}
        renderValue={(value) => {
          const theme = themes.find((t) => t.id === value);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {theme?.icon}
              <Typography>{theme?.name ? t(theme.name) : ""}</Typography>
              <Box sx={{ display: "flex", gap: 0.5, ml: "auto" }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: "4px",
                    backgroundColor: theme?.primaryColor,
                    border: "1px solid #E0E0E0",
                  }}
                />
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: "4px",
                    backgroundColor: theme?.secondaryColor,
                    border: "1px solid #E0E0E0",
                  }}
                />
              </Box>
            </Box>
          );
        }}
      >
        {themes.map((theme) => (
          <MenuItem key={theme.id} value={theme.id}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: 2,
                py: 1,
              }}
            >
              {/* <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  backgroundColor: theme.primaryColor,
                  color: "#FFFFFF",
                }}
              >
                {theme.icon}
              </Box> */}

              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {t(theme.name)}
                </Typography>
                {/* <Typography variant="caption" color="text.secondary">
                  {theme.description}
                </Typography> */}
              </Box>

              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "4px",
                    backgroundColor: theme.primaryColor,
                    border: "1px solid #E0E0E0",
                  }}
                />
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "4px",
                    backgroundColor: theme.secondaryColor,
                    border: "1px solid #E0E0E0",
                  }}
                />
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Select>

      {/* Theme Preview */}
      {/* <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: '8px',
          border: '1px solid #E0E0E0',
          backgroundColor: '#F9FAFB'
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
          Current Theme Preview
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              flex: 1,
              height: 60,
              borderRadius: '8px',
              background: `linear-gradient(135deg, ${selectedTheme.primaryColor} 0%, ${selectedTheme.secondaryColor} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFFFFF',
              fontWeight: 600
            }}
          >
            {selectedTheme.name} Theme
          </Box>
          <Chip
            label="Active"
            size="small"
            sx={{
              backgroundColor: selectedTheme.primaryColor,
              color: '#FFFFFF',
              fontWeight: 500
            }}
          />
        </Box>
      </Box> */}
    </FormControl>
  );
};

export default ThemePicker;
